import React from 'react';
import { useConfigService } from '@thd-nucleus/experience-context';
import { pbPreferredPricing } from '../../../../../constants/PricingBenefits';

interface TextLines {
  line1: string;
  line2?: string;
}

interface BlurbData {
  title: TextLines;
  content: TextLines;
}

interface BlurbTitleProps {
  isMobile: boolean;
  children: React.ReactNode;
}

interface BlurbContentProps {
  content: TextLines;
}

interface BlurbBodyProps {
  isQualified: boolean;
  showRegistrationEntryPoint: boolean;
  isHDPassVerbageEnabled: boolean;
}

interface PrefPricingBlurbsProps {
  isMobile: boolean;
  isQualified: boolean;
  showRegistrationEntryPoint: boolean;
}

const handleMultiLines = (text: TextLines) => (
  <>
    {text.line1}
    {text.line2 && (
      <>
        <br />
        {text.line2}
      </>
    )}
  </>
);

const getBlurbText = (
  isQualified: boolean,
  isHDPassVerbageEnabled: boolean,
  showRegistrationEntryPoint: boolean = false,
) => {
  const ppBlurbs = isHDPassVerbageEnabled
    ? pbPreferredPricing.ppBlurbsHDPass
    : pbPreferredPricing.ppBlurbs;
  const textPreQualified = [ppBlurbs.unlockPrefPricing];
  const savingsThatGrowLine2 = showRegistrationEntryPoint
    ? ''
    : ppBlurbs.savingsThatGrow.content.line2;
  const textQualified = [
    ppBlurbs.shopScanSave,
    ppBlurbs.projectPriority,
    {
      ...ppBlurbs.savingsThatGrow,
      content: {
        ...ppBlurbs.savingsThatGrow.content,
        line2: savingsThatGrowLine2,
      },
    },
  ];
  return isQualified ? textQualified : textPreQualified;
};

const BlurbTitle = ({ isMobile, children }: BlurbTitleProps) => {
  return isMobile ? (
    <div
      className="sui-text-lg sui-font-bold"
      data-testid="PrefPricingBlurbs__BlurbTitle__Mobile"
    >
      {children}
    </div>
  ) : (
    <p
      className="sui-border-b-1 sui-border-solid sui-border-primary sui-pb-1 sui-text-4xl sui-mt-4"
      data-testid="PrefPricingBlurbs__BlurbTitle__Desktop"
    >
      {children}
    </p>
  );
};

const BlurbContent = ({ content }: BlurbContentProps) => (
  <p
    data-testid="BlurbContent"
    className="sui-text-base lg:sui-text-lg sui-py-3"
    style={{ maxWidth: '770px' }}
  >
    {handleMultiLines(content)}
  </p>
);

const BlurbBodyDT = ({
  isQualified,
  showRegistrationEntryPoint,
  isHDPassVerbageEnabled,
}: BlurbBodyProps) => {
  const array = getBlurbText(
    isQualified,
    isHDPassVerbageEnabled,
    showRegistrationEntryPoint,
  );

  return (
    <div className="sui-pt-5">
      {array.map((data: BlurbData, i: number) => (
        <div key={i} className="">
          <BlurbTitle isMobile={false}>
            {data.title.line1}
            {data.title.line2 && ` ${data.title.line2}`}
          </BlurbTitle>
          <BlurbContent content={data.content} />
        </div>
      ))}
    </div>
  );
};

const BlurbBodyMW = ({
  isQualified,
  showRegistrationEntryPoint,
  isHDPassVerbageEnabled,
}: BlurbBodyProps) => {
  const array = getBlurbText(
    isQualified,
    isHDPassVerbageEnabled,
    showRegistrationEntryPoint,
  );
  return (
    <div className="sui-pt-5">
      {array.map((data: BlurbData, i: number) => (
        <div key={i} className="">
          <BlurbTitle isMobile>{handleMultiLines(data.title)}</BlurbTitle>
          <BlurbContent content={data.content} />
        </div>
      ))}
    </div>
  );
};

export const PrefPricingBlurbs = React.memo(
  ({
    isMobile,
    isQualified,
    showRegistrationEntryPoint,
  }: PrefPricingBlurbsProps) => {
    const isHDPassVerbageEnabled = ['1', 1].includes(
      useConfigService('ENABLE_HD_PASS_VERBAGE'),
    );

    return (
      <div className="">
        {isMobile ? (
          <BlurbBodyMW
            isQualified={isQualified}
            showRegistrationEntryPoint={showRegistrationEntryPoint}
            isHDPassVerbageEnabled={isHDPassVerbageEnabled}
          />
        ) : (
          <BlurbBodyDT
            isQualified={isQualified}
            showRegistrationEntryPoint={showRegistrationEntryPoint}
            isHDPassVerbageEnabled={isHDPassVerbageEnabled}
          />
        )}
      </div>
    );
  },
);

PrefPricingBlurbs.displayName = 'PrefPricingBlurbs';
